import React, { useState, useEffect, useRef } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line
} from 'recharts';
import { vertraegeApi, statistikenApi } from '../api';
import { format, parseISO, differenceInDays, addMonths, subMonths } from 'date-fns';
import { de } from 'date-fns/locale';

const FARBEN = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#f97316','#06b6d4'];

export default function AuswertungenPage() {
  const [stats, setStats] = useState(null);
  const [vertraege, setVertraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ansicht, setAnsicht] = useState('monatlich');
  const printRef = useRef();
  const token = localStorage.getItem('token');
  const kalenderUrl = `/api/kalender/export.ics`;

  useEffect(() => {
    Promise.all([statistikenApi.get(), vertraegeApi.list()])
      .then(([s, v]) => { setStats(s.data); setVertraege(v.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Laden...</div>;

  const aktiv = vertraege.filter(v => v.status === 'aktiv');
  const monatlich = stats?.gesamt?.monatliche_kosten || 0;
  const jaehrlich = monatlich * 12;

  // Daten für Balkendiagramm (Kategorie)
  const kategorieData = (stats?.nachKategorie || [])
    .filter(k => k.monatliche_kosten > 0)
    .map(k => ({
      name: `${k.icon} ${k.name}`,
      Monatlich: parseFloat((k.monatliche_kosten || 0).toFixed(2)),
      Jährlich: parseFloat(((k.monatliche_kosten || 0) * 12).toFixed(2)),
      farbe: k.farbe,
    }));

  // Daten für Linienchart (Kostenentwicklung simuliert aus Vertragsdaten)
  const heute = new Date();
  const monateData = Array.from({ length: 12 }, (_, i) => {
    const monat = subMonths(heute, 11 - i);
    const monatStr = format(monat, 'yyyy-MM');
    const kosten = aktiv.reduce((sum, v) => {
      const beginn = v.vertragsbeginn ? parseISO(v.vertragsbeginn) : new Date('2000-01-01');
      const ende = v.vertragsende ? parseISO(v.vertragsende) : new Date('2099-01-01');
      if (beginn <= monat && ende >= monat) {
        const k = v.kosten || 0;
        if (v.zahlungsintervall === 'monatlich') return sum + k;
        if (v.zahlungsintervall === 'jaehrlich') return sum + k / 12;
        if (v.zahlungsintervall === 'vierteljaehrlich') return sum + k / 3;
        if (v.zahlungsintervall === 'halbjaehrlich') return sum + k / 6;
      }
      return sum;
    }, 0);
    return { monat: format(monat, 'MMM yy', { locale: de }), Kosten: parseFloat(kosten.toFixed(2)) };
  });

  // Ablaufende Verträge (nächste 12 Monate)
  const ablaufend = vertraege
    .filter(v => v.naechste_kuendigung && v.status === 'aktiv')
    .map(v => ({ ...v, tage: differenceInDays(parseISO(v.naechste_kuendigung), heute) }))
    .filter(v => v.tage >= -30 && v.tage <= 365)
    .sort((a, b) => a.tage - b.tage);

  const handlePrint = () => window.print();

  return (
    <div style={{ padding: '16px', maxWidth: 700, margin: '0 auto' }} ref={printRef}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700 }}>📊 Auswertungen</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handlePrint} className="btn-primary">🖨️ Drucken / PDF</button>
          <a href={kalenderUrl} download="kuendigungen.ics" style={{...btnStyle('#10b981'), textDecoration: 'none'}}>📅 Kalender Export</a>
        </div>
      </div>

      {/* Kennzahlen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }} className="kpi-grid">
        <KPI label="Verträge aktiv" value={aktiv.length} icon="📋" color="#6366f1" />
        <KPI label="Monatlich" value={euro(monatlich)} icon="📅" color="#10b981" />
        <KPI label="Jährlich" value={euro(jaehrlich)} icon="📆" color="#f59e0b" />
        <KPI label="Bald fällig" value={ablaufend.filter(v => v.tage <= 60).length} icon="⏰" color="#ef4444" />
      </div>

      {/* Umschalt Monatlich/Jährlich */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['monatlich', 'jaehrlich'].map(a => (
          <button key={a} onClick={() => setAnsicht(a)} style={{
            background: ansicht === a ? '#818cf8' : '#1e293b',
            color: ansicht === a ? 'white' : '#94a3b8',
            border: '1px solid #334155', padding: '6px 16px',
            borderRadius: 20, cursor: 'pointer', fontSize: 13,
          }}>
            {a === 'monatlich' ? 'Monatlich' : 'Jährlich'}
          </button>
        ))}
      </div>

      {/* Kosten nach Kategorie – Balken */}
      <Karte titel="💰 Kosten nach Kategorie">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={kategorieData} margin={{ top: 5, right: 10, left: 10, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `${v}€`} />
            <Tooltip formatter={v => euro(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} labelStyle={{ color: '#f1f5f9' }} />
            <Bar dataKey={ansicht === 'monatlich' ? 'Monatlich' : 'Jährlich'} radius={[4, 4, 0, 0]}>
              {kategorieData.map((entry, i) => <Cell key={i} fill={entry.farbe || FARBEN[i % FARBEN.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Karte>

      {/* Kostenverteilung – Donut */}
      <Karte titel="🥧 Kostenverteilung">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <ResponsiveContainer width={180} height={180}>
            <PieChart>
              <Pie data={kategorieData} dataKey="Monatlich" cx="50%" cy="50%" innerRadius={45} outerRadius={75}>
                {kategorieData.map((entry, i) => <Cell key={i} fill={entry.farbe || FARBEN[i % FARBEN.length]} />)}
              </Pie>
              <Tooltip formatter={v => euro(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {kategorieData.map((k, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: k.farbe || FARBEN[i % FARBEN.length] }} />
                  <span style={{ color: '#cbd5e1', fontSize: 13 }}>{k.name}</span>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>{euro(k.Monatlich)}/Mo</span>
                  <span style={{ color: '#f59e0b', fontSize: 12 }}>{euro(k.Jährlich)}/Jahr</span>
                </div>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #334155', paddingTop: 6, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 13 }}>Gesamt</span>
              <div style={{ display: 'flex', gap: 16 }}>
                <span style={{ color: '#10b981', fontSize: 13, fontWeight: 700 }}>{euro(monatlich)}/Mo</span>
                <span style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700 }}>{euro(jaehrlich)}/Jahr</span>
              </div>
            </div>
          </div>
        </div>
      </Karte>

      {/* Kostenentwicklung – Linie */}
      <Karte titel="📈 Kostenentwicklung (letzte 12 Monate)">
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={monateData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="monat" tick={{ fill: '#94a3b8', fontSize: 11 }} />
            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={v => `${v}€`} />
            <Tooltip formatter={v => euro(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} labelStyle={{ color: '#f1f5f9' }} />
            <Line type="monotone" dataKey="Kosten" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1', r: 4 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </Karte>

      {/* Ablaufende Verträge – Zeitstrahl */}
      <Karte titel="⏰ Kündigungsfristen (nächste 12 Monate)">
        {ablaufend.length === 0 ? (
          <p style={{ color: '#64748b', fontSize: 13 }}>Keine Kündigungsfristen in den nächsten 12 Monaten</p>
        ) : (
          ablaufend.map((v, i) => {
            const pct = Math.max(0, Math.min(100, ((v.tage + 30) / 395) * 100));
            const farbe = v.tage < 0 ? '#ef4444' : v.tage <= 30 ? '#f59e0b' : v.tage <= 60 ? '#eab308' : '#10b981';
            return (
              <div key={v.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 500 }}>
                    {v.kategorie_icon || '📄'} {v.titel}
                    <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>{v.anbieter}</span>
                  </span>
                  <span style={{ color: farbe, fontSize: 12, fontWeight: 700 }}>
                    {v.tage < 0 ? 'Überfällig!' : v.tage === 0 ? 'Heute!' : `${v.tage} Tage`}
                    {' · '}{format(parseISO(v.naechste_kuendigung), 'dd.MM.yyyy', { locale: de })}
                  </span>
                </div>
                <div style={{ background: '#334155', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  <div style={{ background: farbe, height: 8, width: `${pct}%`, borderRadius: 4, transition: 'width 0.5s' }} />
                </div>
              </div>
            );
          })
        )}
      </Karte>

      {/* Alle Verträge – Druckliste */}
      <Karte titel="📋 Alle Verträge – Übersicht">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0f172a' }}>
              {['Vertrag', 'Anbieter', 'Kategorie', 'Kosten', 'Intervall', 'Nächste Kündigung', 'Status'].map(h => (
                <th key={h} style={{ padding: '8px 6px', textAlign: 'left', color: '#94a3b8', fontWeight: 600, borderBottom: '1px solid #334155' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vertraege.map((v, i) => (
              <tr key={v.id} style={{ background: i % 2 === 0 ? '#1e293b' : '#0f172a' }}>
                <td style={{ padding: '7px 6px', color: '#f1f5f9' }}>{v.titel}</td>
                <td style={{ padding: '7px 6px', color: '#94a3b8' }}>{v.anbieter || '–'}</td>
                <td style={{ padding: '7px 6px', color: '#94a3b8' }}>{v.kategorie_icon} {v.kategorie_name || '–'}</td>
                <td style={{ padding: '7px 6px', color: '#10b981', fontWeight: 600 }}>{v.kosten ? euro(v.kosten) : '–'}</td>
                <td style={{ padding: '7px 6px', color: '#94a3b8' }}>{zahlungsKurz(v.zahlungsintervall)}</td>
                <td style={{ padding: '7px 6px', color: v.naechste_kuendigung && differenceInDays(parseISO(v.naechste_kuendigung), heute) <= 30 ? '#f87171' : '#94a3b8' }}>
                  {v.naechste_kuendigung ? format(parseISO(v.naechste_kuendigung), 'dd.MM.yyyy', { locale: de }) : '–'}
                </td>
                <td style={{ padding: '7px 6px' }}>
                  <span style={{ background: v.status === 'aktiv' ? '#10b98122' : '#ef444422', color: v.status === 'aktiv' ? '#34d399' : '#f87171', padding: '2px 8px', borderRadius: 10, fontSize: 11 }}>
                    {v.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 12, color: '#64748b', fontSize: 12, textAlign: 'right' }}>
          {vertraege.length} Verträge · Stand: {format(heute, 'dd.MM.yyyy', { locale: de })}
        </div>
      </Karte>

      {/* Print CSS */}
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          header, nav, button { display: none !important; }
          .kpi-grid { grid-template-columns: repeat(4, 1fr) !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function Karte({ titel, children }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: '16px', marginBottom: 16 }}>
      <h3 style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{titel}</h3>
      {children}
    </div>
  );
}

function KPI({ label, value, icon, color }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 10px', textAlign: 'center', border: `1px solid ${color}33` }}>
      <div style={{ fontSize: 20, marginBottom: 4 }}>{icon}</div>
      <div style={{ color, fontWeight: 700, fontSize: 15 }}>{value}</div>
      <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function euro(v) { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0); }
function zahlungsKurz(i) { return { monatlich: 'Monatl.', jaehrlich: 'Jährl.', vierteljaehrlich: 'Quartl.', halbjaehrlich: 'Halbj.', einmalig: 'Einmal' }[i] || i || '–'; }
function btnStyle(color) { return { background: `${color}22`, color, border: `1px solid ${color}44`, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }; }
